import { FormEvent, useEffect, useState } from 'react';
import { ArrowRight, KeyRound, LockKeyhole, Mail, ShieldCheck } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BrandLogo, Button } from '../components/ui';
import { isUnauthorized, testApi } from '../lib/testApi';

export function TestAdminLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let active = true;
    testApi.session().then(() => {
      if (active) navigate(params.get('next') || '/test-admin', { replace: true });
    }).catch((error) => {
      if (active && !isUnauthorized(error)) setMessage('The test console is unavailable right now.');
    });
    return () => { active = false; };
  }, [navigate, params]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      await testApi.login(email.trim(), password);
      navigate(params.get('next') || '/test-admin', { replace: true });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Sign-in failed. Check the account and try again.');
    } finally {
      setBusy(false);
    }
  };

  return <main className="test-login-page">
    <section className="test-login-intro">
      <div className="test-login-brand"><BrandLogo src="/ayurguide-logo.png" className="test-brand-mark" /><div><strong>AyurGuide</strong><small>Test Studio</small></div></div>
      <div className="test-login-copy"><span className="test-kicker">Focused operations workspace</span><h1>Build better tests for every AyurGuide learner.</h1><p>Create question sets, shape assessments, and keep the test library ready for the next study session.</p></div>
      <div className="test-login-note"><ShieldCheck size={18} /><div><strong>Protected AyurGuide workspace</strong><span>Access is checked against your approved test role before any test data is shown.</span></div></div>
    </section>
    <section className="test-login-panel">
      <div className="test-login-panel-head"><span className="test-panel-icon"><LockKeyhole size={18} /></span><div><span className="test-kicker">Welcome back</span><h2>Sign in to Test Studio</h2><p>Use your AyurGuide operations account to continue.</p></div></div>
      <form onSubmit={submit} className="test-login-form">
        <label><span>Email address</span><div className="test-input"><Mail size={17} /><input type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@ayurguide.in" required /></div></label>
        <label><span>Password</span><div className="test-input"><KeyRound size={17} /><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Enter your password" required /></div></label>
        {message && <div className="test-form-error" role="alert">{message}</div>}
        <Button type="submit" icon={ArrowRight} disabled={busy}>{busy ? 'Checking access…' : 'Enter Test Studio'}</Button>
      </form>
      <div className="test-login-footer"><span>AyurGuide production scope</span><span>Secure session</span></div>
    </section>
  </main>;
}
